import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellRing, BellOff, Loader2 } from 'lucide-react';
import { etatPush, activerPush, desactiverPush, EtatPush } from '../utils/push';

const EXPLICATIONS: Partial<Record<EtatPush, string>> = {
  'ecran-accueil-requis':
    "Sur iPhone, ajoute d'abord le dashboard à l'écran d'accueil (Partager → « Sur l'écran d'accueil »), puis ouvre-le depuis l'icône.",
  'non-supporte': 'Ce navigateur ne gère pas les notifications.',
  refuse: 'Notifications refusées : réactive-les dans Réglages → Notifications → Aquarius.',
};

/** Bouton d'activation des notifications, dans le menu mobile ou l'en-tête */
const NotificationsToggle = ({ variante }: { variante: 'menu' | 'icone' }) => {
  const [etat, setEtat] = useState<EtatPush | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    etatPush()
      .then(setEtat)
      .catch(() => setEtat('non-supporte'));
  }, []);

  useEffect(() => {
    if (!message) return;
    const minuterie = setTimeout(() => setMessage(''), 8000);
    return () => clearTimeout(minuterie);
  }, [message]);

  const basculer = async () => {
    if (occupe || !etat) return;
    if (etat !== 'actif' && etat !== 'inactif') {
      setMessage(EXPLICATIONS[etat] || '');
      return;
    }

    setOccupe(true);
    setMessage('');
    try {
      if (etat === 'actif') {
        await desactiverPush();
        setMessage('Notifications désactivées sur cet appareil.');
      } else {
        await activerPush();
        setMessage('Notifications activées : une notification de test arrive.');
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Impossible de modifier les notifications');
    } finally {
      setEtat(await etatPush().catch((): EtatPush => 'non-supporte'));
      setOccupe(false);
    }
  };

  const actif = etat === 'actif';
  const Icone = occupe ? Loader2 : actif ? BellRing : BellOff;
  const libelle = actif ? 'Notifications activées' : 'Activer les notifications';

  if (variante === 'icone') {
    return (
      <div className="relative">
        <button
          onClick={basculer}
          title={libelle}
          aria-label={libelle}
          className={`inline-flex items-center justify-center px-3 py-2 rounded-md text-sm border transition-all duration-200 ${
            actif
              ? 'text-cyan-200 border-cyan-400/40 bg-cyan-500/10 hover:bg-cyan-500/20'
              : 'text-gray-400 border-gray-600/40 hover:text-cyan-300 hover:border-cyan-500/30'
          }`}
        >
          <Icone className={`h-4 w-4 ${occupe ? 'animate-spin' : ''}`} />
        </button>
        {message && (
          <div className="absolute right-0 top-full mt-2 w-64 rounded-lg border border-indigo-500/40 bg-gray-900/95 px-3 py-2 text-xs text-gray-200 shadow-lg">
            {message}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={basculer}
        className={`w-full px-3 py-2 rounded-md text-base font-medium flex items-center space-x-2 transition-all duration-200 border ${
          actif
            ? 'text-cyan-300 bg-gradient-to-r from-cyan-500/10 to-indigo-500/10 border-cyan-500/30'
            : 'text-gray-300 hover:text-cyan-300 border-transparent hover:border-cyan-500/30'
        }`}
      >
        <Icone className={`h-5 w-5 ${occupe ? 'animate-spin' : ''}`} />
        <span>{libelle}</span>
      </button>
      {message && <p className="px-3 pt-1 pb-2 text-xs text-gray-400">{message}</p>}
    </div>
  );
};

/**
 * Clic sur une notification alors que l'app est déjà ouverte : le service
 * worker relaie l'adresse, et on y va sans recharger la page.
 */
export const EcouteClicsNotification = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const surMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'notification-clic') return;
      const cible = new URL(String(event.data.url || '/schedule'), window.location.origin);
      const ouvrirJarvis = cible.searchParams.has('jarvis');
      cible.searchParams.delete('jarvis');
      // L'adresse garde ses paramètres : /schedule?date=… ouvre le planning de ce jour
      navigate(cible.pathname + cible.search);
      if (ouvrirJarvis) window.dispatchEvent(new Event('jarvis:ouvrir'));
    };
    navigator.serviceWorker.addEventListener('message', surMessage);
    return () => navigator.serviceWorker.removeEventListener('message', surMessage);
  }, [navigate]);

  return null;
};

export default NotificationsToggle;
